import { useMemo, useState } from "react";
import styles from "@/components/dataVisualization/donutChart.module.css";

export interface DonutChartSegment {
  id: string;
  label: string;
  value: number;
  color: string;
}

interface TooltipState {
  x: number;
  y: number;
  segment: DonutChartSegment;
}

interface Props {
  segments: DonutChartSegment[];
  ariaLabel: string;
  valueFormatter?: (value: number) => string;
  className?: string;
  trackColor?: string;
  viewBoxSize?: number;
  outerRadius?: number;
  innerRadius?: number;
}

const polarToCartesian = (
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number,
) => {
  const angleInRadians = (angleInDegrees * Math.PI) / 180;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
};

const buildSlicePath = (
  centerX: number,
  centerY: number,
  outerRadius: number,
  innerRadius: number,
  startAngle: number,
  endAngle: number,
) => {
  const safeEndAngle =
    Math.abs(endAngle - startAngle) >= 360 ? startAngle + 359.999 : endAngle;
  const outerStart = polarToCartesian(centerX, centerY, outerRadius, startAngle);
  const outerEnd = polarToCartesian(centerX, centerY, outerRadius, safeEndAngle);
  const innerEnd = polarToCartesian(centerX, centerY, innerRadius, safeEndAngle);
  const innerStart = polarToCartesian(centerX, centerY, innerRadius, startAngle);
  const largeArcFlag = safeEndAngle - startAngle > 180 ? 1 : 0;

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${innerStart.x} ${innerStart.y}`,
    "Z",
  ].join(" ");
};

export default function DonutChart({
  segments,
  ariaLabel,
  valueFormatter = (value) => String(value),
  className,
  trackColor = "#ece6f3",
  viewBoxSize = 320,
  outerRadius = 116,
  innerRadius = 68,
}: Props) {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const center = viewBoxSize / 2;
  const totalValue = useMemo(
    () => segments.reduce((sum, segment) => sum + segment.value, 0),
    [segments],
  );
  const nonEmptySegments = useMemo(
    () => segments.filter((segment) => segment.value > 0),
    [segments],
  );

  return (
    <div
      className={`${styles.wrapper} ${className ?? ""}`.trim()}
      onMouseLeave={() => setTooltip(null)}
    >
      <svg
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        className={styles.svg}
        role="img"
        aria-label={ariaLabel}
      >
        {totalValue === 0 ? (
          <circle
            cx={center}
            cy={center}
            r={outerRadius}
            fill="none"
            stroke={trackColor}
            strokeWidth={outerRadius - innerRadius}
          />
        ) : (
          (() => {
            let currentAngle = -90;

            return nonEmptySegments.map((segment) => {
              const sliceAngle = (segment.value / totalValue) * 360;
              const startAngle = currentAngle;
              const endAngle = startAngle + sliceAngle;
              currentAngle = endAngle;
              const path = buildSlicePath(
                center,
                center,
                outerRadius,
                innerRadius,
                startAngle,
                endAngle,
              );

              return (
                <path
                  key={segment.id}
                  d={path}
                  fill={segment.color}
                  stroke="#f6f6f6"
                  strokeWidth="2"
                  onMouseMove={(event) => {
                    const bounds =
                      event.currentTarget.ownerSVGElement?.getBoundingClientRect();
                    if (!bounds) {
                      return;
                    }
                    setTooltip({
                      x: event.clientX - bounds.left,
                      y: event.clientY - bounds.top,
                      segment,
                    });
                  }}
                  aria-label={`${segment.label}: ${valueFormatter(segment.value)}`}
                />
              );
            });
          })()
        )}
      </svg>

      {tooltip ? (
        <div
          className={styles.tooltip}
          style={{
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <span
            className={styles.tooltipDot}
            style={{ backgroundColor: tooltip.segment.color }}
          />
          <span>{tooltip.segment.label}</span>
          <strong>{valueFormatter(tooltip.segment.value)}</strong>
        </div>
      ) : null}
    </div>
  );
}
